import { saveAuthVault, loadAuthVault, clearAuthVault } from "@/lib/authVault";
import { resetAuthRefreshState } from "@/lib/authRefreshState";
import { upsertSavedAccount, migrateCurrentSessionToAccounts, initAccountsCrypto } from "@/lib/authAccounts";

const TOKEN_KEY = "mycoaching_token";
const TOKEN_EXP_KEY = "mycoaching_token_exp";
const USER_KEY = "mycoaching_user";
const SESSION_BLOB_KEY = "mycoaching_session";
const COOKIE_KEY = "mc_session";
const REMEMBER_KEY = "mycoaching_remember_me";
const REMEMBER_EMAIL_KEY = "mycoaching_remembered_email";
/** Même couche que l'email — survit au boot iOS quand le token seul est purgé. */
const REMEMBER_SESSION_KEY = "mycoaching_remembered_session";
const LOGOUT_FLAG_KEY = "mycoaching_logged_out";
const DB_NAME = "mycoaching_auth";
const DB_STORE = "session";
const SESSION_MAX_AGE = 90 * 86400;

let initPromise = null;
let dbPromise = null;

export function markLoggedOut() {
  try {
    localStorage.setItem(LOGOUT_FLAG_KEY, String(Date.now()));
    sessionStorage.setItem(LOGOUT_FLAG_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function isLoggedOut() {
  try {
    return (
      localStorage.getItem(LOGOUT_FLAG_KEY) != null ||
      sessionStorage.getItem(LOGOUT_FLAG_KEY) != null
    );
  } catch {
    return false;
  }
}

export function clearLoggedOutFlag() {
  try {
    localStorage.removeItem(LOGOUT_FLAG_KEY);
    sessionStorage.removeItem(LOGOUT_FLAG_KEY);
  } catch {
    /* ignore */
  }
}

function getDb() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(DB_STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }).catch(() => null);
  }
  return dbPromise;
}

async function idbSet(key, value) {
  try {
    const db = await getDb();
    if (!db) return;
    await new Promise((resolve, reject) => {
      const tx = db.transaction(DB_STORE, "readwrite");
      tx.objectStore(DB_STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* IndexedDB unavailable */
  }
}

async function idbGet(key) {
  try {
    const db = await getDb();
    if (!db) return null;
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(DB_STORE, "readonly");
      const req = tx.objectStore(DB_STORE).get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function idbRemove(key) {
  try {
    const db = await getDb();
    if (!db) return;
    await new Promise((resolve, reject) => {
      const tx = db.transaction(DB_STORE, "readwrite");
      tx.objectStore(DB_STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* ignore */
  }
}

function getCookieDomain() {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  if (host === "mycoaching.be" || host.endsWith(".mycoaching.be")) {
    return "; domain=.mycoaching.be";
  }
  return "";
}

function getCookieToken() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_KEY}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setClientCookie(token, maxAge = SESSION_MAX_AGE) {
  if (typeof document === "undefined" || !token) return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(token)}; path=/; max-age=${maxAge}; SameSite=Lax${secure}${getCookieDomain()}`;
}

function clearClientCookie() {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax${secure}${getCookieDomain()}`;
}

export function getRememberMe() {
  return localStorage.getItem(REMEMBER_KEY) === "1";
}

export function setRememberMe(remember) {
  localStorage.setItem(REMEMBER_KEY, remember ? "1" : "0");
  if (!remember) {
    localStorage.removeItem(REMEMBER_EMAIL_KEY);
  }
}

export function getRememberedEmail() {
  if (!getRememberMe()) return "";
  return localStorage.getItem(REMEMBER_EMAIL_KEY) || "";
}

export function setRememberedEmail(email) {
  if (getRememberMe() && email) {
    localStorage.setItem(REMEMBER_EMAIL_KEY, email);
  }
}

export function getRememberedSession() {
  if (!getRememberMe()) return null;
  try {
    const raw = localStorage.getItem(REMEMBER_SESSION_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function setRememberedSession(token, user) {
  if (!getRememberMe() || !token) return;
  try {
    const payload = { token, t: Date.now() };
    if (user?.user_id) {
      const { token: _t, ...userRest } = user;
      payload.user = userRest;
    }
    localStorage.setItem(REMEMBER_SESSION_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function loadSessionBlobSync() {
  try {
    const raw = localStorage.getItem(SESSION_BLOB_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function saveSessionBlob(token, user) {
  if (!token) return;
  try {
    const payload = { token, t: Date.now() };
    if (user?.user_id) {
      const { token: _t, ...userRest } = user;
      payload.user = userRest;
    }
    localStorage.setItem(SESSION_BLOB_KEY, JSON.stringify(payload));
    setRememberedSession(token, user);
  } catch {
    /* ignore */
  }
}

function applyRestoredSession(token, user) {
  if (!token) return null;
  if (!localStorage.getItem(TOKEN_KEY)) {
    localStorage.setItem(TOKEN_KEY, token);
  }
  if (user && !localStorage.getItem(USER_KEY)) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
  return { token, user: user ?? null };
}

/** Restauration synchrone avant le 1er render React (comme l'email mémorisé). */
export function restoreSessionSync() {
  if (isLoggedOut()) return null;
  migrateLegacyToken();

  const blob = loadSessionBlobSync();
  if (blob?.token) {
    return applyRestoredSession(blob.token, blob.user ?? null);
  }

  const remembered = getRememberedSession();
  if (remembered?.token) {
    return applyRestoredSession(remembered.token, remembered.user ?? null);
  }

  const token = localStorage.getItem(TOKEN_KEY) || getCookieToken();
  const user = getUserCacheSync();
  if (token && user) {
    return { token, user };
  }
  if (token) {
    return { token, user: null };
  }
  return null;
}

export function hasStoredSession() {
  if (isLoggedOut()) return false;
  const blob = loadSessionBlobSync();
  const remembered = getRememberedSession();
  return !!(
    blob?.token ||
    remembered?.token ||
    localStorage.getItem(TOKEN_KEY) ||
    getCookieToken()
  );
}

export function migrateLegacyToken() {
  const legacy = localStorage.getItem("token");
  if (legacy && !localStorage.getItem(TOKEN_KEY)) {
    saveToken(legacy);
    localStorage.removeItem("token");
  }
}

export function saveToken(token, options = {}) {
  if (!token) return;
  clearLoggedOutFlag();
  const maxAge = options.maxAge ?? SESSION_MAX_AGE;
  localStorage.setItem(TOKEN_KEY, token);
  setClientCookie(token, maxAge);
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload?.exp) {
      localStorage.setItem(TOKEN_EXP_KEY, String(payload.exp * 1000));
    }
  } catch {
    localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + maxAge * 1000));
  }
  void idbSet(TOKEN_KEY, token);
}

export function saveUserCache(user) {
  if (!user?.user_id) return;
  const { token: _t, ...rest } = user;
  try {
    const json = JSON.stringify(rest);
    localStorage.setItem(USER_KEY, json);
    void idbSet(USER_KEY, json);
  } catch {
    /* ignore */
  }
}

async function syncVault() {
  const token = localStorage.getItem(TOKEN_KEY) || getCookieToken();
  const userRaw = localStorage.getItem(USER_KEY);
  if (!token) return;
  let user = null;
  try {
    user = userRaw ? JSON.parse(userRaw) : null;
  } catch {
    user = null;
  }
  await saveAuthVault({
    token,
    user,
    remember_me: getRememberMe(),
  });
}

export function getToken() {
  if (isLoggedOut()) return null;
  migrateLegacyToken();
  let token = localStorage.getItem(TOKEN_KEY);
  if (token) return token;

  const blob = loadSessionBlobSync();
  if (blob?.token) {
    localStorage.setItem(TOKEN_KEY, blob.token);
    return blob.token;
  }

  token = getCookieToken();
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    return token;
  }

  return null;
}

export function getUserCacheSync() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (raw) return JSON.parse(raw);
    const blob = loadSessionBlobSync();
    if (blob?.user) return blob.user;
  } catch {
    /* ignore */
  }
  return null;
}

export async function getUserCache() {
  const sync = getUserCacheSync();
  if (sync) return sync;

  try {
    const fromIdb = await idbGet(USER_KEY);
    if (fromIdb) {
      localStorage.setItem(USER_KEY, fromIdb);
      return JSON.parse(fromIdb);
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function hasAuthHint() {
  return hasStoredSession() || !!localStorage.getItem(USER_KEY);
}

export async function initAuthStorage() {
  if (isLoggedOut()) return;
  if (!initPromise) {
    initPromise = (async () => {
      if (isLoggedOut()) return;
      restoreSessionSync();

      if (!localStorage.getItem(TOKEN_KEY)) {
        const fromCookie = getCookieToken();
        if (fromCookie) saveToken(fromCookie);
      }

      if (!localStorage.getItem(TOKEN_KEY)) {
        const fromIdb = await idbGet(TOKEN_KEY);
        if (fromIdb) saveToken(fromIdb);
      }

      if (!localStorage.getItem(TOKEN_KEY)) {
        const vault = await loadAuthVault();
        if (vault?.token) {
          saveToken(vault.token);
          if (vault.user) {
            saveUserCache(vault.user);
            saveSessionBlob(vault.token, vault.user);
          }
        }
      }

      const token = getToken();
      const cachedUser = getUserCacheSync();
      if (token && cachedUser) {
        saveSessionBlob(token, cachedUser);
      }

      await initAccountsCrypto();
    })();
  }
  return initPromise;
}

export async function flushAuthStorage() {
  if (isLoggedOut()) return;
  const token = localStorage.getItem(TOKEN_KEY) || getCookieToken();
  let user = null;
  try {
    const raw = localStorage.getItem(USER_KEY);
    user = raw ? JSON.parse(raw) : getUserCacheSync();
  } catch {
    user = getUserCacheSync();
  }
  if (token) {
    setClientCookie(token);
    await idbSet(TOKEN_KEY, token);
  }
  if (user) {
    await idbSet(USER_KEY, JSON.stringify(user));
    if (token) saveSessionBlob(token, user);
  }
  await syncVault();
}

export async function requestPersistentStorage() {
  try {
    if (navigator.storage?.persist) {
      await navigator.storage.persist();
    }
  } catch {
    /* ignore */
  }
}

function clearLocalAuthKeys() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXP_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(SESSION_BLOB_KEY);
  localStorage.removeItem(REMEMBER_SESSION_KEY);
  localStorage.removeItem("token");
  clearClientCookie();
}

export async function initAuthStorageWithTimeout(ms = 4000) {
  return Promise.race([
    initAuthStorage(),
    new Promise((resolve) => setTimeout(resolve, ms)),
  ]);
}

/** Déconnexion complète — attendre avant de naviguer vers /login. */
export async function clearAuthSession() {
  markLoggedOut();
  clearLocalAuthKeys();
  initPromise = null;
  await Promise.all([idbRemove(TOKEN_KEY), idbRemove(USER_KEY), clearAuthVault()]);
}

/** Sauvegarde le compte actif dans le coffre multi-comptes avant une nouvelle connexion. */
export function preserveActiveAccountForMultiLogin() {
  const token = getToken();
  const user = getUserCacheSync();
  if (token && user?.user_id) {
    upsertSavedAccount(token, user);
  }
}

/** Purge toute session locale avant une nouvelle connexion (changement de compte). */
export async function prepareAuthForLogin() {
  resetAuthRefreshState();
  clearLoggedOutFlag();
  bumpAuthStorageGeneration();
  clearLocalAuthKeys();
  initPromise = null;
  await Promise.all([idbRemove(TOKEN_KEY), idbRemove(USER_KEY), clearAuthVault()]);
}

/** Débloque la page /login quand un flag de déconnexion ou une session fantôme bloque l'accès. */
export function unlockLoginPage() {
  clearLoggedOutFlag();
}

/** @deprecated Utiliser clearAuthSession() */
export function clearToken() {
  markLoggedOut();
  clearLocalAuthKeys();
  initPromise = null;
  void idbRemove(TOKEN_KEY);
  void idbRemove(USER_KEY);
  void clearAuthVault();
}

export function isTokenExpiringSoon(thresholdMs = 14 * 86400000) {
  const exp = Number(localStorage.getItem(TOKEN_EXP_KEY) || 0);
  if (!exp) return false;
  return exp - Date.now() < thresholdMs;
}

function flushSessionSync() {
  if (isLoggedOut()) return;
  const token = localStorage.getItem(TOKEN_KEY) || getCookieToken();
  if (!token) return;
  let user = null;
  try {
    const raw = localStorage.getItem(USER_KEY);
    user = raw ? JSON.parse(raw) : getUserCacheSync();
  } catch {
    user = getUserCacheSync();
  }
  saveSessionBlob(token, user);
  setClientCookie(token);
}

export function setupAuthPersistenceHooks() {
  if (typeof window === "undefined") return;

  const flush = () => {
    if (isLoggedOut()) return;
    flushSessionSync();
    flushAuthStorage();
  };

  window.addEventListener("pagehide", flush);
  window.addEventListener("beforeunload", () => {
    if (!isLoggedOut()) flushSessionSync();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}

export async function persistAuthSession(data, rememberMe = true) {
  resetAuthRefreshState();
  clearLoggedOutFlag();
  setRememberMe(rememberMe);
  bumpAuthStorageGeneration();
  clearLocalAuthKeys();
  initPromise = null;
  await Promise.all([idbRemove(TOKEN_KEY), idbRemove(USER_KEY), clearAuthVault()]);

  if (data?.user_id) {
    saveUserCache(data);
  }
  if (data?.token) {
    const maxAge = rememberMe ? SESSION_MAX_AGE : 86400;
    saveToken(data.token, { maxAge });
    if (data.user_id) {
      saveSessionBlob(data.token, data);
      upsertSavedAccount(data.token, data);
    }
  }
  await requestPersistentStorage();
  flushSessionSync();
  await flushAuthStorage();
}

export { migrateCurrentSessionToAccounts };

let storageInitGeneration = 0;

export function bumpAuthStorageGeneration() {
  storageInitGeneration += 1;
  initPromise = null;
  return storageInitGeneration;
}

export function getAuthStorageGeneration() {
  return storageInitGeneration;
}